import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/generated/types";
import { useProject } from "@/pages/projects/hooks/useProject";
import { useDeleteProject } from "@/pages/projects/hooks/useDeleteProject";
import { ErrorPage, ConfirmDialog } from "@/components/shared";
import { ProjectProvider } from "../contexts/ProjectContext";
import ProjectHeaderWithTabs, {
  type ProjectDetailTab,
} from "./ProjectHeaderWithTabs";
import ProjectShellSkeleton from "./ProjectShellSkeleton";

interface ProjectShellProps {
  projectId: string;
  activeTab: ProjectDetailTab;
  children: React.ReactNode;
}

/**
 * Shared layout shell for all project detail tab pages.
 * Handles project loading, error states, header, tabs, and delete confirmation.
 * Renders ProjectShellSkeleton as an outer safeguard while loading.
 * Exposes the loaded project and refetch function to children via render prop.
 */
export default function ProjectShell({
  projectId,
  activeTab,
  children,
}: ProjectShellProps) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { project, loading, error, refetch } = useProject(projectId);
  const onDelete = useCallback(() => navigate("/dashboard"), [navigate]);
  const { deleteProject, loading: deleteLoading } = useDeleteProject({
    onCompleted: onDelete,
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = useCallback(() => {
    deleteProject(projectId);
  }, [deleteProject, projectId]);

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
  }, []);

  const canDelete = !!(
    currentUser?.role === UserRole.Admin &&
    project?.creator?.id === currentUser.id
  );

  const canManageMembers = useMemo(
    () =>
      !!(
        (currentUser?.role === UserRole.Admin ||
          currentUser?.role === UserRole.Collaborator) &&
        project?.members?.some((m) => m.id === currentUser.id)
      ),
    [currentUser, project],
  );

  const isAdmin = currentUser?.role === UserRole.Admin;
  const showFinancialTab = !!(project?.hasFinancialAccess || isAdmin);

  if (loading) {
    return <ProjectShellSkeleton />;
  }

  if (error) {
    return <ErrorPage message={`Erro: ${error.message}`} />;
  }

  if (!project) {
    return <ErrorPage message="Projeto não encontrado" />;
  }

  return (
    <div className="min-h-screen bg-primary-50 dark:bg-primary-950 py-24">
      <div className="container-elegant max-w-4xl">
        <ProjectHeaderWithTabs
          projectId={projectId}
          project={project}
          activeTab={activeTab}
          canDelete={canDelete}
          canManageMembers={canManageMembers}
          hasFinancialAccess={project.hasFinancialAccess}
          showFinancialTab={showFinancialTab}
          onDeleteClick={handleDeleteClick}
        />

        <ProjectProvider value={{ project, refetch }}>
          {children}
        </ProjectProvider>

        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Excluir Projeto"
          message={`Tem certeza de que deseja excluir "${project.name}"? Esta ação não pode ser desfeita.`}
          confirmLabel="EXCLUIR"
          confirmLoadingLabel="EXCLUINDO..."
          onConfirm={handleDelete}
          loading={deleteLoading}
          destructive
        />
      </div>
    </div>
  );
}
